
import { jsPDF } from "jspdf";
import { QuizResult, ContactInfo } from "@/types";
import { 
  addHeader, 
  addIntroduction,
  addAssessmentResults, 
  addStrengthsAndImprovements, 
  addActionPlan, 
  addGlossary,
  addResources, 
  addConclusion,
  addFooter 
} from "./sections";

/**
 * Generates a customized AI Governance Report PDF
 * @param result The quiz result data
 * @param contactInfo The user's contact information
 * @returns A jsPDF document object
 */
export const generateAIGovernanceReport = (
  result: QuizResult,
  contactInfo: ContactInfo
): jsPDF => {
  // Initialize PDF document with Techne color theme
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  
  // Colors matching the site theme
  const techneColors = {
    purple: [189, 71, 230],        // #bd47e6
    primaryPurple: [103, 36, 125], // #67247d
    deepPurple: [39, 13, 55],      // #270d37
    royalPurple: [67, 21, 88],     // #431558
    black: [4, 1, 1],              // #040101
    white: [255, 255, 255],        // #ffffff
  };
  
  // Add report header (cover page) with Techne colors
  addHeader(doc, { 
    companyName: contactInfo.company, 
    pageWidth,
    colors: techneColors
  });
  
  // Add introduction section
  let yPosition = 70;
  yPosition = addIntroduction({ doc, pageWidth, y: yPosition, colors: techneColors });
  
  // Add assessment results section
  yPosition = addAssessmentResults({ doc, pageWidth, y: yPosition, colors: techneColors }, result.percentage);
  
  // Add strengths and improvements
  yPosition = addStrengthsAndImprovements(
    { doc, pageWidth, y: yPosition, colors: techneColors }, 
    result.strengths,
    result.improvements
  );
  
  // Add action plan page with table
  addActionPlan(doc, { improvements: result.improvements, pageWidth, colors: techneColors });
  
  // Add glossary section
  addGlossary(doc, { pageWidth, colors: techneColors });
  
  // Add resources section
  addResources(doc, { startY: 60, pageWidth, colors: techneColors });
  
  // Add conclusion
  addConclusion(doc, { pageWidth, colors: techneColors });
  
  // Add footer ONLY to the last page
  const lastPage = doc.getNumberOfPages();
  doc.setPage(lastPage);
  const footerY = doc.internal.pageSize.height - 20;
  addFooter(doc, { companyName: contactInfo.company, pageWidth, footerY, colors: techneColors });
  
  return doc;
};
